import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { EFileService } from '../../efile.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { Submission } from '../../models/submission.model';
import { SubmissionService } from '../../submission-data/submission.service';
import {
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-efile-xml-sync',
    templateUrl: './xml-sync.component.html',
    styleUrls: ['./xml-sync.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class XmlSyncComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  private static readonly JCD_KEY_FEDERAL = 250;

  /** Active submission row from `submission-detail` (legacy `EfileFactory.selectedSubmission`). */
  @Input() submission: Submission | any;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  rowData: any = {};
  filterParams: any = {};
  syncingXML = false;
  isSynced = false;

  constructor(
    private efileService: EFileService,
    private snackbar: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private submissionService: SubmissionService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['submission']) {
      this.applyWorkspaceContext();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    this.efileService.setBaseUrls(this.baseURLs);
    this.filterParams = normalizeFilterParams(activeScreen);
    // Prefer the submission passed by `submission-detail` (legacy
    // `EfileFactory.selectedSubmission`). Workspace grid selection is
    // empty on this screen, which is why taxYear/submissionId/scenario
    // were blank on the sync API call.
    this.rowData =
      this.submission && (this.submission as any).SUBMISSION_ID
        ? this.submission
        : this.submissionService?.activeSubmission?.SUBMISSION_ID
          ? this.submissionService.activeSubmission
          : rowPayloadFromActiveScreen(activeScreen);
  }

  /**
   * Build `/copyFederalXMLtoS3` query params — mirrors
   * `XMLSyncController#syncXML` (EfileElementsController.js L3816-3820).
   */
  private buildSyncParams(): {
    taxYear: string;
    submissionId: string;
    scenario: string;
    jcdKey: string;
  } {
    const src = this.rowData || {};
    const taxYear = String(
      src.TAX_YEAR ??
        this.filterParams?.tax_year ??
        this.gtwGlobal.global?.globalParams?.tax_year ??
        '',
    );
    const submissionId = String(src.SUBMISSION_ID ?? '');
    const jcdKey = String(
      src.JCD_KEY ??
        this.filterParams?.jcd_key ??
        this.gtwGlobal.global?.globalParams?.jcd_key ??
        XmlSyncComponent.JCD_KEY_FEDERAL,
    );
    const scenario = String(
      src.SCENARIO ??
        src.SCENARIO_ID ??
        this.filterParams?.scenario ??
        this.resolveDefaultScenario(taxYear) ??
        this.gtwGlobal.global?.globalParams?.scenario ??
        '',
    );
    return { taxYear, submissionId, scenario, jcdKey };
  }

  /**
   * Legacy scenario lookup from `GlobalService.inputs.tax_year.values`
   * (EfileElementsController.js L3816-3819): default federal scenario for
   * the active tax year where `jcd_key === 250` and `default_scenario === 'Y'`.
   */
  private resolveDefaultScenario(taxYear: string): string | number | undefined {
    const values: any[] = (this.gtwGlobal.global as any)?.inputs?.tax_year?.values || [];
    const ty = String(taxYear ?? '');
    const found = _.find(values, { label: ty }) || _.find(values, (v: any) => String(v?.value) === ty);
    const scenarios: any[] = found?.scenarios || [];
    const def = _.find(
      scenarios,
      (s: any) =>
        Number(s?.jcd_key) === XmlSyncComponent.JCD_KEY_FEDERAL && s?.default_scenario === 'Y',
    );
    return def?.scenario;
  }

  syncXML(): void {
    if (this.syncingXML || this.isSynced) return;

    const { taxYear, submissionId, scenario, jcdKey } = this.buildSyncParams();
    if (!taxYear || !submissionId || !scenario) {
      this.snackbar.openSnackBar(
        'Unable to sync — submission context is missing.',
        'gtw-snackbar--red',
      );
      return;
    }

    this.syncingXML = true;

    this.efileService.syncXML(taxYear, submissionId, scenario, jcdKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.syncingXML = false;
        if (res?.callSuccess === '1' || res?.data?.callSuccess === '1') {
          this.isSynced = true;
          this.snackbar.openSnackBar('Successfully Synced the XML with SLT!', 'gtw-snackbar--green');
        } else {
          this.snackbar.openSnackBar(res?.errorMessage || res?.data?.errorMessage || 'Failed to Sync XML with SLT!', 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.syncingXML = false;
        this.snackbar.openSnackBar('Failed to Sync XML with SLT!', 'gtw-snackbar--red');
      }
    });
  }
}
